import React, {useEffect, useState, useRef} from "react";
import ReactDOM from "react-dom";
import {useIntl} from "react-intl";
import classes from "./styles.module.css";

var MONTH_NAMES_RU = [
    "январь", "февраль", "март", "апрель", "май", "июнь",
    "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"
];

var MONTH_NAMES_EN = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

var DAY_NAMES_RU = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
var DAY_NAMES_EN = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function pad(n) {
    return n < 10 ? "0" + n : "" + n;
}

function isSameDay(a, b) {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
}

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
    var d = new Date(year, month, 1).getDay();
    return d === 0 ? 6 : d - 1;
}

function buildCalendarGrid(year, month) {
    var daysInMonth = getDaysInMonth(year, month);
    var firstDay = getFirstDayOfWeek(year, month);
    var prevMonth = month === 0 ? 11 : month - 1;
    var prevYear = month === 0 ? year - 1 : year;
    var daysInPrev = getDaysInMonth(prevYear, prevMonth);

    var cells = [];

    for (var i = firstDay - 1; i >= 0; i--) {
        cells.push({
            day: daysInPrev - i,
            month: prevMonth,
            year: prevYear,
            outside: true
        });
    }

    for (var d = 1; d <= daysInMonth; d++) {
        cells.push({day: d, month: month, year: year, outside: false});
    }

    var remaining = 42 - cells.length;
    var nextMonth = month === 11 ? 0 : month + 1;
    var nextYear = month === 11 ? year + 1 : year;
    for (var j = 1; j <= remaining; j++) {
        cells.push({day: j, month: nextMonth, year: nextYear, outside: true});
    }

    return cells;
}

function formatDisplay(date, dateFormat) {
    if (!date) return "";
    var d = pad(date.getDate());
    var m = pad(date.getMonth() + 1);
    var y = date.getFullYear();
    if (dateFormat === "yyyy-MM-dd") return y + "-" + m + "-" + d;
    return d + "." + m + "." + y;
}

/**
 * SingleDatePicker — выбор одной даты.
 *
 * Props:
 *   selected     — Date object или null
 *   onChange      — function(date: Date)
 *   dateFormat    — "dd.MM.yyyy" (default) или "yyyy-MM-dd"
 *   disabled      — boolean
 *   placeholderText — string
 *   className     — дополнительный CSS-класс для input
 */
function SingleDatePicker(props) {
    var selected = props.selected;
    var onChange = props.onChange;
    var dateFormat = props.dateFormat || "dd.MM.yyyy";
    var disabled = props.disabled || false;
    var placeholderText = props.placeholderText || "";
    var extraClass = props.className || "";

    var intl = useIntl();
    var isRu = intl.locale === "ru";
    var monthNames = isRu ? MONTH_NAMES_RU : MONTH_NAMES_EN;
    var dayNames = isRu ? DAY_NAMES_RU : DAY_NAMES_EN;

    var _open = useState(false);
    var isOpen = _open[0];
    var setIsOpen = _open[1];

    var _viewMonth = useState(function () {
        var d = selected instanceof Date && !isNaN(selected) ? selected : new Date();
        return {year: d.getFullYear(), month: d.getMonth()};
    });
    var viewMonth = _viewMonth[0];
    var setViewMonth = _viewMonth[1];

    var _dropdownPos = useState({top: 0, left: 0});
    var dropdownPos = _dropdownPos[0];
    var setDropdownPos = _dropdownPos[1];

    var triggerRef = useRef(null);
    var dropdownRef = useRef(null);

    useEffect(function () {
        function handleClickOutside(e) {
            var inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
            var inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!inTrigger && !inDropdown) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return function () {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    function openDropdown() {
        if (disabled) return;
        if (!isOpen && triggerRef.current) {
            var rect = triggerRef.current.getBoundingClientRect();
            var panelWidth = 212;
            var left = rect.left;
            if (left + panelWidth > window.innerWidth) {
                left = window.innerWidth - panelWidth - 8;
            }
            if (left < 8) left = 8;
            var top = rect.bottom + 4;
            if (top + 300 > window.innerHeight) {
                top = rect.top - 300 - 4;
                if (top < 8) top = 8;
            }
            setDropdownPos({top: top, left: left});
            if (selected instanceof Date && !isNaN(selected)) {
                setViewMonth({year: selected.getFullYear(), month: selected.getMonth()});
            }
        }
        setIsOpen(!isOpen);
    }

    function prevMonth() {
        setViewMonth(function (prev) {
            var m = prev.month - 1;
            var y = prev.year;
            if (m < 0) { m = 11; y--; }
            return {year: y, month: m};
        });
    }

    function nextMonth() {
        setViewMonth(function (prev) {
            var m = prev.month + 1;
            var y = prev.year;
            if (m > 11) { m = 0; y++; }
            return {year: y, month: m};
        });
    }

    function handleDayClick(cell) {
        var d = new Date(cell.year, cell.month, cell.day);
        if (onChange) onChange(d);
        setIsOpen(false);
    }

    function handleMonthSelect(e) {
        var m = parseInt(e.target.value, 10);
        setViewMonth(function (prev) { return {year: prev.year, month: m}; });
    }

    function handleYearSelect(e) {
        var y = parseInt(e.target.value, 10);
        setViewMonth(function (prev) { return {year: y, month: prev.month}; });
    }

    var grid = buildCalendarGrid(viewMonth.year, viewMonth.month);
    var weeks = [];
    for (var w = 0; w < 6; w++) {
        weeks.push(grid.slice(w * 7, (w + 1) * 7));
    }

    var currentYear = new Date().getFullYear();
    var years = [];
    for (var yr = currentYear - 100; yr <= currentYear + 10; yr++) {
        years.push(yr);
    }

    var inputClasses = classes.TriggerInput;
    if (extraClass) inputClasses = extraClass;
    if (isOpen) inputClasses += " " + classes.TriggerInputActive;
    if (disabled) inputClasses += " " + classes.TriggerInputDisabled;

    return (
        <div className={classes.Wrap}>
            <input
                ref={triggerRef}
                type="text"
                readOnly
                className={inputClasses}
                value={formatDisplay(selected, dateFormat)}
                placeholder={placeholderText}
                onClick={openDropdown}
                disabled={disabled}
            />
            {isOpen && ReactDOM.createPortal(
                <div ref={dropdownRef} className={classes.Dropdown}
                     style={{top: dropdownPos.top, left: dropdownPos.left}}>
                    <div className={classes.Selectors}>
                        <select className={classes.Select} value={viewMonth.month}
                                onChange={handleMonthSelect}>
                            {monthNames.map(function (name, i) {
                                return <option key={i} value={i}>{name}</option>;
                            })}
                        </select>
                        <select className={classes.Select} value={viewMonth.year}
                                onChange={handleYearSelect}>
                            {years.map(function (y) {
                                return <option key={y} value={y}>{y}</option>;
                            })}
                        </select>
                    </div>
                    <div className={classes.MonthHeader}>
                        <button className={classes.NavBtn} onClick={prevMonth} type="button">←</button>
                        <span className={classes.MonthTitle}>
                            {monthNames[viewMonth.month]} {viewMonth.year}
                        </span>
                        <button className={classes.NavBtn} onClick={nextMonth} type="button">→</button>
                    </div>
                    <div className={classes.DayNames}>
                        {dayNames.map(function (name, i) {
                            return (
                                <span key={i} className={i >= 5 ? classes.Weekend : ""}>
                                    {name}
                                </span>
                            );
                        })}
                    </div>
                    <div className={classes.DaysGrid}>
                        {weeks.map(function (week, wi) {
                            return week.map(function (cell, di) {
                                var cellDate = new Date(cell.year, cell.month, cell.day);
                                var isSelected = selected && isSameDay(cellDate, selected);

                                var cellClasses = classes.Day;
                                if (cell.outside) cellClasses += " " + classes.Outside;
                                if (isSelected) cellClasses += " " + classes.Selected;

                                return (
                                    <span
                                        key={wi + "-" + di}
                                        className={cellClasses}
                                        onClick={function () { handleDayClick(cell); }}
                                    >
                                        {cell.day}
                                    </span>
                                );
                            });
                        })}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default SingleDatePicker;



// WEBPACK FOOTER //
// ./src/Components/Utils/SingleDatePicker/index.js